import { Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { AlertsService } from '@services/alerts.service';
import { IntercomService } from '@services/intercom.service';
import { WebSocketService } from '@services/web-socket.service';
import { Subject, take, takeUntil } from 'rxjs';

@Component({
  selector: 'app-alerts',
  templateUrl: './alerts.component.html',
  styleUrl: './alerts.component.scss'
})
export class AlertsComponent implements OnInit {
  public searchBy = "name";
  public totalCount = 0;
  public catalogState$;
  public isProcessing;
  public pageNumber = 1;
  public perPage = 10;
  public itemCount = 0;
  public isLoading = true;
  public rows: any = [];
  public filterInput = "";
  public statusFilter = "active";
  public sortBy:string;
  public sortAsc = false;
  alerts: any[] = [];
  public statusControl = this.fb.control("active");
  public statusControl$;
  public $destroy = new Subject();
  public statusOptions = [
    { value: "active", text: "Active" },
    { value: "inactive", text: "Resolved" },
    { value: "all", text: "All" },
  ];

  public columns = [
    {
      property: "type",
      translationKey: "alert_type",
      textTransform: "uppercase",
      width: "150px",
      isSortable: true,
    },
    {
      type: "locallink",
      property: "location",
      translationKey: "location",
      textTransform: "uppercase",
      width: "150px",
      isSortable: true,
    },
    {
      property: "status",
      translationKey: "status",
      textTransform: "uppercase",
      isSortable: true,
      width: "120px",
    },
    {
      property: "level",
      translationKey: "level",
      textTransform: "uppercase",
      isSortable: true,
      width: "120px",
      getClass: (value: any) => {
        return value.toLowerCase() === "critical" ? "critical-alert" : "";
      }
    },
    {
      property: "message",
      translationKey: "description",
      textTransform: "uppercase",
      isSortable: true,
    },
    {
      type: "date",
      property: "created",
      translationKey: "occured",
      textTransform: "uppercase",
      width: "150px",
      isSortable: true,
    },
    {
      type: "date",
      property: "inactive_at",
      translationKey: "resolved",
      textTransform: "uppercase",
      width: "150px",
      isSortable: true,
      undefinedText: "N/A"
    },
    {
      type: "template",
      property: "action",
      translationKey: "action_required",
      textTransform: "uppercase",
      width: "200px",
      isSortable: false,
    },
  ];

  constructor(
    private alertsService: AlertsService,
    private fb: FormBuilder,
    private intercomService: IntercomService,
    private translateService: TranslateService,
    private webSocketService: WebSocketService
  ) { }

  ngOnInit() {
    this.statusControl.valueChanges.pipe(takeUntil(this.$destroy)).subscribe((value) => {
      this.pageNumber = 1;
      this.statusFilter = value;
      this.getAlerts();
    });
    this.getAlerts();
    this.webSocketService.subscribeToMessages('alert').subscribe((res) => {
      console.log(res);
      this.getAlerts();
    });
  }
  getAlerts() {
    console.log(this.filterInput, this.statusControl.value, this.pageNumber, this.perPage, this.sortBy, this.sortAsc);
    this.alertsService.getAlerts(this.filterInput, this.statusControl.value, this.pageNumber, this.perPage, this.sortBy, this.sortAsc).pipe(take(1)).subscribe((res: any) => {
      if(res.listItems.length === 0){
        this.alerts = [this.generateExampleResult()];
      }else{
        this.alerts = res.listItems.map((item: any) => {
          return {
          ...item, 
          status: this.translateService.instant(item.inactive_at ? "resolved" : "active"), 
          action: this.generateAction(item),
          level: item.level ? this.translateService.instant(item.level) : "",
          type: item.type ? this.translateService.instant(item.type) : "",
          location: this.generateLocationLink(item),
          }
        });
      }
      this.itemCount = res.total || 0;
      this.isLoading = false;
    });
  }

  ngOnDestroy() {
    this.$destroy.next(true);
    this.$destroy.complete();
  }

  generateAction(item) {    
    let action;
    if(item.action_required){ 
      if(item.action_required.match(/support/i)){
        action = {text: item.action_required, viewType: "button-link"}
      }else if (item.type === "blueprint") {
        action = { text: item.action_required, viewType: "locallink", link: this.generateLocationLink(item).link }
      } else {
        action = { text: item.action_required, viewType: "text" }
      }

    } else {
      action = {text: 'No Action Required', viewType: "button-link"}
    }
    return action;
  }

  generateExampleResult() {
    let item ={
      type: "example",
      location: "123456",
      status: "inactive",
      level: "critical",
      message: "This is an example of how an alert message will look",
      created: "2024-11-01",
      inactive_at: "2024-11-02",
      action_required: "Contact Support",
    }
    return {
      ...item, 
      status: this.translateService.instant(item.inactive_at ? "resolved" : "active"), 
      action: this.generateAction(item),
      level: item.level ? this.translateService.instant(item.level) : "",
      type: item.type ? this.translateService.instant(item.type) : "",
      location: this.generateLocationLink(item),
    }
  }

  generateLocationLink(item) {
    let link = "";
    let text = "";
    switch(item.type) {
      case "integration":
        link = isNaN(item.location) ? `/admin/integrations/edit/${item.location}` : `/admin/integrations`;
        text = "Integration";
        break;
      case "blueprint":
        link = `/documentation/catalog/${item.location}`;
        text = item.location;
        break;
      default:
        link = "";
        text = item.location;
    }
    return {link, text};
  }

  capitalizeEachWord(str) {
    const words = str.split(" ");
    const capitalizedWords = words.map(
      (word) => word.charAt(0).toUpperCase() + word.slice(1).toLocaleLowerCase()
    );
    return capitalizedWords.join(" ");
  }

  initSearch($event) {
    this.filterInput = $event;
    this.pageNumber = 1;
    this.getAlerts();
  }
  setPageCount($event) {
    this.pageNumber = 1;
    this.perPage = $event;
    this.getAlerts();
  }
  sortChange($event) {
    this.pageNumber = 1;
    this.sortAsc = $event.property === this.sortBy ? !this.sortAsc : true;
    this.sortBy = $event.property;
    this.getAlerts();
  }
  activePageChange($event) {
    this.pageNumber = $event;
    this.getAlerts();
  }
  linkClicked(link) {
    if (link.property === "action") {
      if(link.row.action_required.match(/support/i)){
        this.intercomService.startTicket();
      }
    }
  }
}
